import { LoaderCircle } from "lucide-mui"
import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react"
import { useOnWindowResize } from "rooks"

import { calculateDimensions } from "@/components/calendar_view/_dimensions"
import ClosedLayer from "@/components/calendar_view/layers/closed"
import CurrentTimeDotLayer from "@/components/calendar_view/layers/current_time/dot_layer"
import CurrentTimeLineLayer from "@/components/calendar_view/layers/current_time/line_layer"
import CurrentTimePillLayer from "@/components/calendar_view/layers/current_time/pill_layer"
import EmptyStateLayer from "@/components/calendar_view/layers/empty_state"
import ReservationsLayer from "@/components/calendar_view/layers/reservations"
import SelectionLayer from "@/components/calendar_view/layers/selection"
import Preview from "@/components/calendar_view/preview"
import { useVenue } from "@/contexts/venue"
import { GetReservationsQuery } from "@/graphql/graphql"
import { useDelayedRendering } from "@/shared/hooks"
import { useStore } from "@/store"

import Container from "./container"
import TablesLayer from "./layers/tables"
import TimelineLayer from "./layers/timeline"
import TableGroupsHeader from "./table_groups_header"
import TableHeader from "./table_header"

type CalendarViewProps = {
  reservations: GetReservationsQuery["venue"]["reservations"]["nodes"]
  loading: boolean
  startHour: number
  endHour: number
  openingStartHour: number
  openingEndHour: number
  closed: boolean
}

type MainCanvasContainerProps = {
  dimensions: ReturnType<typeof calculateDimensions>
} & React.ComponentProps<"div">

const MainCanvasContainer = React.forwardRef<HTMLDivElement, MainCanvasContainerProps>(
  ({ dimensions, ...props }, ref) => (
    <div
      className="flex-1 overflow-auto bg-white"
      style={{
        width: dimensions.canvasContainer.width,
        height: dimensions.canvasContainer.height,
      }}
      ref={ref}
      {...props}
    />
  ),
)

MainCanvasContainer.displayName = "MainCanvasContainer"
const TimelineContainer = (props) => <div className="flex w-full relative" {...props} />

const CalendarFrame: React.FC<React.PropsWithChildren> = ({ children }) => (
  <div className="relative w-full overflow-hidden rounded-md bg-white" data-testid="panel-reservations-calendar-frame">
    {children}
    <div className="pointer-events-none absolute inset-0 z-40 rounded-md border border-solid border-gray-300" />
  </div>
)

// spinner with 25% opacity white background container
const Loading = () => {
  const ready = useDelayedRendering(100)

  if (!ready) return null

  return (
    <div className="absolute inset-0 bg-white bg-opacity-50 flex items-center justify-center z-50">
      <div className="animate-spin text-5xl text-gray-800 opacity-30">
        <LoaderCircle fontSize="inherit" className="block" />
      </div>
    </div>
  )
}

const TimelineTimeColumn = ({ dimensions }) => {
  return (
    <div
      className="w-20 bg-white border-0 border-r border-solid border-gray-300 flex-shrink-0"
      style={{
        width: dimensions.timeline.columnWidth,
        height: dimensions.viewport.height,
      }}
    />
  )
}
const MainCanvas = ({ dimensions, ...props }) => {
  return (
    <div
      className="flex-1 min-w-min relative"
      style={{
        width: dimensions.canvas.width,
        height: dimensions.canvas.height,
      }}
      {...props}
    />
  )
}

const CalendarView = ({
  reservations,
  loading,
  startHour,
  endHour,
  openingStartHour,
  openingEndHour,
  closed,
}: CalendarViewProps) => {
  const { venue } = useVenue()
  const {
    fullWidthMode,
    selectedTableGroupIds,
    calendarDimensions: dimensions,
    setCalendarDimensions: setDimensions,
  } = useStore()

  const containerRef = useRef<HTMLDivElement>(null)
  const mainCanvasRef = useRef<HTMLDivElement>(null)
  const [focused, setFocused] = useState({ reservation: null, target: null })
  const [selectionActive, setSelectionActive] = useState(false)

  const tables = useMemo(
    () => venue.tables.filter((table) => !table.deleted && selectedTableGroupIds.includes(table.tableGroup.id)),
    [selectedTableGroupIds, venue.tables],
  )

  const onReservationFocus = useCallback((reservation, target) => {
    setFocused({ reservation: reservation.id, target })
  }, [])
  const clearReservationFocus = useCallback(() => {
    setFocused({ reservation: null, target: null })
  }, [])

  const getDimensions = () =>
    calculateDimensions({
      container: containerRef.current,
      venue,
      selectedTableGroupIds,
      startHour,
      endHour,
    })

  useLayoutEffect(() => {
    setDimensions(getDimensions())
  }, [selectedTableGroupIds, fullWidthMode, startHour, endHour])

  useOnWindowResize(() => {
    setDimensions(getDimensions())
  })

  const visibleTableIds = useMemo(() => new Set(tables.map((t) => t.id)), [tables])
  const visibleReservations = useMemo(
    () => reservations?.filter((r) => visibleTableIds.has(r.table.id)) ?? [],
    [reservations, visibleTableIds],
  )
  const focusedReservation = focused.reservation ? visibleReservations.find((r) => r.id === focused.reservation) : null

  useEffect(() => {
    if (!focusedReservation) {
      clearReservationFocus()
    }
  }, [focusedReservation])

  return (
    <Container ref={containerRef} className="bg-white" data-testid="panel-reservations-calendar">
      <CalendarFrame>
        <TimelineContainer>
          <TimelineLayer startHour={startHour} endHour={endHour} mainCanvasRef={mainCanvasRef} />
          <CurrentTimePillLayer startHour={startHour} endHour={endHour} mainCanvasRef={mainCanvasRef} />
          <CurrentTimeDotLayer startHour={startHour} endHour={endHour} mainCanvasRef={mainCanvasRef} />
          <TimelineTimeColumn dimensions={dimensions} />
          <MainCanvasContainer dimensions={dimensions} ref={mainCanvasRef}>
            <TableGroupsHeader />
            <TableHeader tables={tables} />
            <MainCanvas dimensions={dimensions}>
              <TablesLayer tables={tables} />
              <CurrentTimeLineLayer startHour={startHour} endHour={endHour} />
              <SelectionLayer
                tables={tables}
                startHour={startHour}
                endHour={endHour}
                onSelectionActiveChange={setSelectionActive}
              />
              <ReservationsLayer reservations={visibleReservations} onReservationFocus={onReservationFocus} />
              <ClosedLayer
                closed={closed}
                startHour={startHour}
                endHour={endHour}
                openingStartHour={openingStartHour}
                openingEndHour={openingEndHour}
              />
              {!loading && !selectionActive && visibleReservations.length === 0 && (
                <EmptyStateLayer tables={tables} startHour={startHour} endHour={endHour} />
              )}
            </MainCanvas>
          </MainCanvasContainer>
          {loading ? <Loading /> : null}
        </TimelineContainer>
      </CalendarFrame>

      <Preview target={focused.target} reservation={focusedReservation} venue={venue} onClose={clearReservationFocus} />
    </Container>
  )
}

export default CalendarView
